import React from 'react';
import { Microscope, Atom, Target, Shield, Brain, Activity, Building2, FlaskRound as Flask, CheckCircle2, AlertTriangle, Rocket, Lightbulb } from 'lucide-react';
import './Mkd45.css';

const Mkd45: React.FC = () => {
  return (
    <div className="mkd45">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>BNCT Technology Database</h1>
          <p>Advanced Cancer Treatment Research & Development</p>
        </div>
      </div>

      {/* Video Section */}
      <section className="video-section container">
        <div className="video-container">
          <iframe
            src="https://www.youtube.com/embed/399OlHiC4AE"
            title="BNCT Technology Overview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </section>

      {/* Introduction */}
      <section className="intro-section container">
        <div className="section-header">
          <h2>Understanding BNCT</h2>
          <p>Boron Neutron Capture Therapy: A Revolutionary Cancer Treatment</p>
        </div>
        <div className="intro-grid">
          <div className="intro-card">
            <Microscope className="intro-icon" />
            <h3>20+ Years of Research</h3>
            <p>Collaborative research between Tsinghua University and Kyoto University has advanced BNCT technology.</p>
          </div>
          <div className="intro-card">
            <Atom className="intro-icon" />
            <h3>Nuclear Technology</h3>
            <p>Evolution from reactor-based to accelerator-based neutron generation for improved accessibility.</p>
          </div>
          <div className="intro-card">
            <Target className="intro-icon" />
            <h3>Precision Treatment</h3>
            <p>Targeted radiation therapy using boron-10 for selective cancer cell destruction.</p>
          </div>
        </div>
      </section>

      {/* Key Components */}
      <section className="components-section">
        <div className="container">
          <div className="section-header">
            <h2>Key Components of BNCT</h2>
            <p>Essential Elements for Successful Treatment</p>
          </div>
          <div className="components-grid">
            <div className="component-card">
              <Flask className="component-icon" />
              <h3>Boron Compound</h3>
              <ul className="component-list">
                <li>Specialized boron-10 isotope</li>
                <li>BPA (Boronophenylalanine)</li>
                <li>BSH (Sodium Borocaptate)</li>
                <li>Selective accumulation in cancer cells</li>
              </ul>
            </div>

            <div className="component-card">
              <Atom className="component-icon" />
              <h3>Neutron Source</h3>
              <ul className="component-list">
                <li>Modern accelerator-based systems</li>
                <li>Controlled neutron generation</li>
                <li>Enhanced safety features</li>
                <li>Improved accessibility</li>
              </ul>
            </div>

            <div className="component-card">
              <Activity className="component-icon" />
              <h3>Treatment Mechanism</h3>
              <ul className="component-list">
                <li>Neutron capture reaction</li>
                <li>Alpha particle generation</li>
                <li>Lithium nuclei production</li>
                <li>Localized cell destruction</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="advantages-section container">
        <div className="section-header">
          <h2>Advantages of BNCT</h2>
          <p>Leading Benefits of This Advanced Treatment</p>
        </div>
        <div className="advantages-grid">
          <div className="advantage-card">
            <Target className="advantage-icon" />
            <h3>Highly Targeted</h3>
            <p>Minimizes damage to healthy cells through precise targeting of cancer cells.</p>
          </div>
          <div className="advantage-card">
            <Shield className="advantage-icon" />
            <h3>One-Time Treatment</h3>
            <p>Often requires only one or two sessions compared to conventional radiation therapy.</p>
          </div>
          <div className="advantage-card">
            <Brain className="advantage-icon" />
            <h3>Effective for Resistant Cancers</h3>
            <p>Successfully treats glioblastomas and recurrent head and neck cancers.</p>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="challenges-section">
        <div className="container">
          <div className="section-header">
            <h2>Current Challenges</h2>
            <p>Ongoing Research and Development Focus Areas</p>
          </div>
          <div className="challenges-grid">
            <div className="challenge-card">
              <AlertTriangle className="challenge-icon" />
              <h3>Technical Challenges</h3>
              <ul className="challenge-list">
                <li>Neutron source accessibility</li>
                <li>Boron drug delivery optimization</li>
                <li>Treatment planning complexity</li>
              </ul>
            </div>
            <div className="challenge-card">
              <Building2 className="challenge-icon" />
              <h3>Infrastructure Needs</h3>
              <ul className="challenge-list">
                <li>Specialized facility requirements</li>
                <li>Equipment maintenance</li>
                <li>Staff training programs</li>
              </ul>
            </div>
            <div className="challenge-card">
              <CheckCircle2 className="challenge-icon" />
              <h3>Regulatory Requirements</h3>
              <ul className="challenge-list">
                <li>Clinical trial protocols</li>
                <li>Safety standards compliance</li>
                <li>Treatment validation processes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Future Prospects */}
      <section className="future-section container">
        <div className="section-header">
          <h2>Future Prospects</h2>
          <p>The Evolution of BNCT Technology</p>
        </div>
        <div className="future-grid">
          <div className="future-card">
            <Rocket className="future-icon" />
            <h3>Technology Advancement</h3>
            <p>Development of more efficient and compact neutron sources for wider accessibility.</p>
          </div>
          <div className="future-card">
            <Flask className="future-icon" />
            <h3>Drug Development</h3>
            <p>Research into improved boron compounds for better tumor targeting.</p>
          </div>
          <div className="future-card">
            <Lightbulb className="future-icon" />
            <h3>Clinical Applications</h3>
            <p>Expansion into new cancer types and treatment protocols.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mkd45;